import React, { useMemo, useRef } from "react";
import { Box, Button, HStack, Icon, Link, Spinner, Text, VStack } from "@chakra-ui/react";
import { FiRefreshCw } from "react-icons/fi";
import { epochConverterWithTimeZone } from "utils/dateUtils";
import {
  ArtifactProto,
  ExecutionStepProto,
  ResponseMessageProto,
  scenarioOutput_ScenarioStatusToJSON,
} from "build-ts/chat_session";
import { ExecutionStatusType } from "build-ts/common";
import { ArtifactOpenOpts, ChatMessage, ReasoningGroup, TimelineEntry } from "../../interfaces";
import { ScheduledJobProto } from "build-ts/schedule";
import { AppIcons } from "constants/icons";
import { MarkdownRenderer } from "../../common/MarkdownRenderer";
import { ArtifactLink } from "../../common/ArtifactLink";
import {
  findWorkflowArtifactByDarwinName,
  findKGDataframeArtifactByTableName,
  findChartArtifactByName,
  findReportArtifactByName,
} from "modules/artifacts/utils/markdownLinkUtils";
import ExecutionStepsCard from "modules/chat/components/ExecutionStepsCard";
import { ARTIFACT_TAG_RE, ARTIFACT_TAG_TYPE } from "modules/chat/utils/chatUtils";
import {
  getDataframeTagLinkPresentation,
  getOpenOptsForResolvedArtifact,
  getFileArtifactChipLabel,
  ARTIFACT_TAG_META,
  ArtifactKind,
} from "modules/artifacts/utils/artifactUtils";

interface MessageProps {
  author: "user" | "ai";
  content: string;
  responseMessage?: ResponseMessageProto | undefined;
  artifacts?: ArtifactProto[];
  onArtifactClick?: (artifact: ArtifactProto, opts?: ArtifactOpenOpts) => void;
  chatMessages?: ChatMessage[];
  isLoading: boolean;
  isLastMessage: boolean;
  lsTraceUrl?: string;
  isArtifactsPanelVisible?: boolean;
  /** Resolves any artifact/sub-resource name to its openable ArtifactProto; forwarded to MarkdownRenderer. */
  lookupByName?: (name: string, kind?: ArtifactKind) => ArtifactProto | undefined;
  userTimestampMs?: number;
  executionStatus?: ExecutionStatusType;
  streamingStatusLabel?: string;
  executionSteps?: ExecutionStepProto[];
  /** Reasoning thought blocks accumulated from REASONING-type streaming tokens. */
  streamingReasoningGroups?: ReasoningGroup[];
  /** Unified timeline order for the streaming case — passed straight through to ExecutionStepsCard. */
  timelineOrder?: TimelineEntry[];
  /** Timestamp (ms) of the AI proto message — used to compute the first reasoning block's duration on reload. */
  prevMessageEndTimestampMs?: number;
  scenarioJobs?: ScheduledJobProto[];
  /** User's preferred timezone for formatting message timestamps */
  defaultTimeZone?: string;
  /** Called when the user clicks "Start New Chat" on a failed message */
  onNewChat?: () => void;
  /** ArtifactProtos for files uploaded with this human message — rendered as chips above the message. */
  fileArtifacts?: ArtifactProto[];
}

/**
 * Parse [[type: body]] tags in a user message and return an array of
 * React nodes — plain strings interleaved with clickable ArtifactLink chips.
 *
 * Workflow tags are resolved via scenarioJobs (keyed by ID).
 * All other tag types (dataframe, chart, code, report) are resolved via
 * lookupByName, which searches the session's artifact map by name.
 */
function renderUserMessageTags(
  content: string,
  ctx: {
    scenarioJobs?: ScheduledJobProto[];
    executionSteps?: ExecutionStepProto[];
    artifacts?: ArtifactProto[];
    chatMessages?: ChatMessage[];
    onArtifactClick?: (artifact: ArtifactProto, opts?: ArtifactOpenOpts) => void;
    lookupByName?: (name: string, kind?: ArtifactKind) => ArtifactProto | undefined;
  }
): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  const re = new RegExp(ARTIFACT_TAG_RE.source, "gi");
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;

  while ((match = re.exec(content)) !== null) {
    if (match.index > lastIndex) {
      parts.push(content.slice(lastIndex, match.index));
    }

    const tagType = match[1].toLowerCase();
    const tagName = match[2].trim();

    let artifact: ArtifactProto | undefined;
    let displayName: string;

    if (tagType === ARTIFACT_TAG_TYPE.workflow) {
      // Workflow body format: "darwinName|header.name" — darwin name is before the pipe,
      // header.name is after (used as display label fallback when artifact not resolved).
      const pipeIdx = tagName.indexOf("|");
      const darwinName = pipeIdx !== -1 ? tagName.slice(0, pipeIdx).trim() : tagName;
      const encodedLabel = pipeIdx !== -1 ? tagName.slice(pipeIdx + 1).trim() : tagName;
      artifact = darwinName
        ? findWorkflowArtifactByDarwinName(darwinName, {
            executionSteps: ctx.executionSteps,
            messageArtifacts: ctx.artifacts,
            chatMessages: ctx.chatMessages,
            scenarioJobs: ctx.scenarioJobs,
          })
        : undefined;
      // Prefer the resolved artifact's header name, fall back to the encoded label.
      displayName = artifact?.scenario_job?.header?.name ?? encodedLabel;
    } else {
      // dataframe, chart, code, report — body is just the artifact name.
      // tagType comes from ARTIFACT_TAG_RE, so it is a valid ArtifactKind.
      if (tagType === ARTIFACT_TAG_TYPE.dataframe) {
        artifact =
          ctx.lookupByName?.(tagName, ARTIFACT_TAG_TYPE.dataframe) ??
          findKGDataframeArtifactByTableName(tagName, {
            executionSteps: ctx.executionSteps,
            messageArtifacts: ctx.artifacts,
            chatMessages: ctx.chatMessages,
          });
      } else if (tagType === ARTIFACT_TAG_TYPE.chart) {
        artifact =
          ctx.lookupByName?.(tagName, ARTIFACT_TAG_TYPE.chart) ??
          findChartArtifactByName(tagName, {
            executionSteps: ctx.executionSteps,
            messageArtifacts: ctx.artifacts,
            chatMessages: ctx.chatMessages,
          });
      } else if (tagType === ARTIFACT_TAG_TYPE.report) {
        artifact =
          ctx.lookupByName?.(tagName, ARTIFACT_TAG_TYPE.report) ??
          findReportArtifactByName(tagName, {
            executionSteps: ctx.executionSteps,
            messageArtifacts: ctx.artifacts,
            chatMessages: ctx.chatMessages,
          });
      } else {
        artifact = ctx.lookupByName?.(tagName, tagType as ArtifactKind);
      }
      displayName = tagName;
    }

    const opts =
      tagType === ARTIFACT_TAG_TYPE.dataframe
        ? getDataframeTagLinkPresentation(artifact, tagName).opts
        : getOpenOptsForResolvedArtifact(artifact, tagType as ArtifactKind, tagName);

    const isActive = !!artifact && !!ctx.onArtifactClick;
    const tagIcon =
      tagType === ARTIFACT_TAG_TYPE.dataframe
        ? getDataframeTagLinkPresentation(artifact, tagName).icon
        : AppIcons[(ARTIFACT_TAG_META[tagType] ?? ARTIFACT_TAG_META.workflow).icon];

    parts.push(
      <ArtifactLink key={key++} isActive={isActive} onClick={() => artifact && ctx.onArtifactClick!(artifact, opts)}>
        <Icon as={tagIcon} boxSize="1em" flexShrink={0} />
        {displayName}
      </ArtifactLink>
    );

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < content.length) {
    parts.push(content.slice(lastIndex));
  }

  return parts;
}

const Message: React.FC<MessageProps> = ({
  author,
  content,
  responseMessage,
  artifacts,
  onArtifactClick,
  chatMessages,
  isLoading,
  isLastMessage,
  lsTraceUrl,
  isArtifactsPanelVisible,
  lookupByName,
  userTimestampMs,
  executionStatus,
  scenarioJobs,
  streamingStatusLabel = "Thinking ...",
  executionSteps,
  streamingReasoningGroups,
  timelineOrder,
  prevMessageEndTimestampMs,
  defaultTimeZone,
  onNewChat,
  fileArtifacts,
}) => {
  const mountTimeRef = useRef(Date.now());

  // Preserve fallback timestamp across renders so user-message props remain stable.
  const formattedUserTime = useMemo(
    () =>
      defaultTimeZone
        ? epochConverterWithTimeZone(userTimestampMs ?? mountTimeRef.current, "h:mm a, d MMM", defaultTimeZone)
        : undefined,
    [defaultTimeZone, userTimestampMs]
  );

  const isAiStreaming = author === "ai" && isLastMessage && isLoading;
  // Show the reasoning panel only when there are actual tool-call steps (not just
  // EXECUTING_LLM_CALL markers, which are stored solely for timestamp tracking).
  const hasToolCallSteps = executionSteps?.some(s => s.status !== ExecutionStatusType.EXECUTING_LLM_CALL) ?? false;
  const hasReasoningGroups = (streamingReasoningGroups?.length ?? 0) > 0;
  const showReasoningPanel = author === "ai" && (hasToolCallSteps || isAiStreaming || hasReasoningGroups);
  const hasStreamingAssistantText = Boolean(
    (content?.trim()?.length ?? 0) > 0 ||
    (responseMessage?.scenario_output?.findings?.trim()?.length ?? 0) > 0 ||
    (responseMessage?.scenario_synthesis_output?.output_string?.trim()?.length ?? 0) > 0
  );

  return (
    <HStack alignSelf={author === "user" ? "flex-end" : "flex-start"} w={author === "user" ? "auto" : "100%"}>
      <VStack align={author === "user" ? "flex-end" : "flex-start"} spacing={0} w={author === "user" ? "auto" : "100%"}>
        {author === "user" && fileArtifacts && fileArtifacts.length > 0 && (
          <HStack flexWrap="wrap" spacing={2} justifyContent="flex-end" mb={1}>
            {fileArtifacts.map((artifact, i) => {
              const chipLabel = getFileArtifactChipLabel(artifact);
              const isClickable = !!onArtifactClick;
              return (
                <HStack
                  key={artifact.file_artifact?.header?.id ?? `${chipLabel}-${i}`}
                  bg="white"
                  border="1px solid"
                  borderColor="purple.200"
                  borderRadius="lg"
                  px={3}
                  py={1.5}
                  spacing={1.5}
                  maxW="260px"
                  cursor={isClickable ? "pointer" : undefined}
                  _hover={isClickable ? { borderColor: "purple.400", bg: "purple.50" } : undefined}
                  onClick={isClickable ? () => onArtifactClick(artifact) : undefined}
                >
                  <Icon as={AppIcons.upload} boxSize={3.5} color="purple.500" flexShrink={0} />
                  <Text fontSize="xs" fontWeight="500" color="purple.700" m={0} noOfLines={1}>
                    {chipLabel}
                  </Text>
                </HStack>
              );
            })}
          </HStack>
        )}
        {author === "user" && !content?.trim() ? null : (
          <Box
            bg={author === "user" ? "purple.50" : "white"}
            px={3}
            py={2}
            borderRadius="lg"
            w={author === "ai" ? "100%" : "auto"}
            maxW="100%"
            flex="1"
            ml={author === "user" ? "3" : "0"}
            mb={author === "user" && formattedUserTime ? 0 : 3}
          >
            {isLoading && isLastMessage && !content && !responseMessage ? (
              author === "ai" ? (
                <Box w="100%">
                  <ExecutionStepsCard
                    onArtifactClick={onArtifactClick}
                    executionSteps={executionSteps}
                    streamingReasoningGroups={streamingReasoningGroups}
                    timelineOrder={timelineOrder}
                    prevMessageEndTimestampMs={prevMessageEndTimestampMs}
                    isStreaming
                    hasStreamingAssistantText={hasStreamingAssistantText}
                  />
                </Box>
              ) : (
                <HStack spacing={2} align="center">
                  <Spinner size="sm" color="purple.500" thickness="2px" />
                  <Text fontSize={isArtifactsPanelVisible ? "sm" : "md"} color="gray.600" mb={0}>
                    {streamingStatusLabel}
                  </Text>
                </HStack>
              )
            ) : executionStatus === ExecutionStatusType.TOOL_CALL_FAILURE ? (
              // Error state
              <VStack align="start" spacing={3} py={1}>
                <HStack spacing={2}>
                  <Text fontSize={isArtifactsPanelVisible ? "sm" : "md"} color="red.500" fontWeight="600" mb={0}>
                    An error occurred. Please start a new chat.
                  </Text>
                </HStack>
                <Button
                  size="sm"
                  colorScheme="purple"
                  leftIcon={<Icon as={FiRefreshCw} boxSize={3.5} />}
                  onClick={() => onNewChat?.()}
                  fontWeight="600"
                >
                  Start New Chat
                </Button>
              </VStack>
            ) : (
              // Normal message state
              <VStack align="start" spacing={0} w="100%">
                {author === "ai" && (
                  <VStack w="100%">
                    <HStack w="100%" justify="flex-end">
                      {/* LangSmith trace link for Eva */}
                      {lsTraceUrl && (
                        <Link
                          href={lsTraceUrl}
                          isExternal
                          fontSize={isArtifactsPanelVisible ? "xs" : "sm"}
                          color="purple.600"
                          fontWeight="bold"
                          _hover={{ color: "purple.700", textDecoration: "none" }}
                        >
                          Trace
                        </Link>
                      )}
                    </HStack>
                  </VStack>
                )}
                {author === "ai" && responseMessage?.scenario_output && (
                  <VStack align="start" spacing={2} w="100%" mt={2}>
                    {showReasoningPanel ? (
                      <Box w="100%">
                        <ExecutionStepsCard
                          onArtifactClick={onArtifactClick}
                          executionSteps={executionSteps}
                          streamingReasoningGroups={streamingReasoningGroups}
                          timelineOrder={timelineOrder}
                          prevMessageEndTimestampMs={prevMessageEndTimestampMs}
                          isStreaming={isAiStreaming}
                          hasStreamingAssistantText={hasStreamingAssistantText}
                        />
                      </Box>
                    ) : null}
                    <Box textAlign="left">
                      <Text fontSize={isArtifactsPanelVisible ? "sm" : "md"} fontWeight="bold" mb={1}>
                        Status:{" "}
                        {responseMessage.scenario_output.status !== undefined
                          ? scenarioOutput_ScenarioStatusToJSON(responseMessage.scenario_output.status)
                          : "N/A"}
                      </Text>
                    </Box>
                    <Box>
                      <Text fontSize={isArtifactsPanelVisible ? "sm" : "md"} fontWeight="bold" mb={1} textAlign="left">
                        Findings:
                      </Text>
                      <Text fontSize={isArtifactsPanelVisible ? "sm" : "md"} m={0} textAlign="left">
                        <MarkdownRenderer
                          content={responseMessage.scenario_output.findings || "N/A"}
                          artifacts={artifacts}
                          executionSteps={executionSteps}
                          isArtifactsPanelVisible={isArtifactsPanelVisible}
                          lookupByName={lookupByName}
                          onArtifactClick={onArtifactClick}
                          chatMessages={chatMessages}
                          scenarioJobs={scenarioJobs}
                        />
                      </Text>
                    </Box>
                  </VStack>
                )}
                {author === "ai" && responseMessage?.scenario_synthesis_output && (
                  <VStack align="start" spacing={2} w="100%" mt={2}>
                    {showReasoningPanel ? (
                      <Box w="100%">
                        <ExecutionStepsCard
                          onArtifactClick={onArtifactClick}
                          executionSteps={executionSteps}
                          streamingReasoningGroups={streamingReasoningGroups}
                          timelineOrder={timelineOrder}
                          prevMessageEndTimestampMs={prevMessageEndTimestampMs}
                          isStreaming={isAiStreaming}
                          hasStreamingAssistantText={hasStreamingAssistantText}
                        />
                      </Box>
                    ) : null}
                    <Box>
                      <Text fontSize={isArtifactsPanelVisible ? "md" : "sm"} fontWeight="bold" mb={1} textAlign="left">
                        Output String:
                      </Text>
                      <Text fontSize={isArtifactsPanelVisible ? "md" : "sm"} m={0} textAlign="left">
                        <MarkdownRenderer
                          content={responseMessage.scenario_synthesis_output.output_string || "N/A"}
                          artifacts={artifacts}
                          executionSteps={executionSteps}
                          isArtifactsPanelVisible={isArtifactsPanelVisible}
                          lookupByName={lookupByName}
                          onArtifactClick={onArtifactClick}
                          chatMessages={chatMessages}
                          scenarioJobs={scenarioJobs}
                        />
                      </Text>
                    </Box>
                    <Box>
                      <Text fontSize={isArtifactsPanelVisible ? "md" : "sm"} fontWeight="bold" mb={1} textAlign="left">
                        Notify:
                      </Text>
                      <Text fontSize={isArtifactsPanelVisible ? "md" : "sm"} m={0} textAlign="left">
                        {responseMessage.scenario_synthesis_output.notify ? "Yes" : "No"}
                      </Text>
                    </Box>
                  </VStack>
                )}
                {author === "ai" &&
                  !responseMessage?.scenario_output &&
                  !responseMessage?.scenario_synthesis_output && (
                    <VStack align="start" spacing={1} w="100%">
                      {showReasoningPanel ? (
                        <Box w="100%">
                          <ExecutionStepsCard
                            onArtifactClick={onArtifactClick}
                            executionSteps={executionSteps}
                            streamingReasoningGroups={streamingReasoningGroups}
                            timelineOrder={timelineOrder}
                            prevMessageEndTimestampMs={prevMessageEndTimestampMs}
                            isStreaming={isAiStreaming}
                            hasStreamingAssistantText={hasStreamingAssistantText}
                          />
                        </Box>
                      ) : null}
                      <Box w="100%" mx={isArtifactsPanelVisible ? 1 : 2}>
                        <MarkdownRenderer
                          content={content}
                          artifacts={artifacts}
                          executionSteps={executionSteps}
                          isArtifactsPanelVisible={isArtifactsPanelVisible}
                          lookupByName={lookupByName}
                          onArtifactClick={onArtifactClick}
                          chatMessages={chatMessages}
                          scenarioJobs={scenarioJobs}
                        />
                      </Box>
                    </VStack>
                  )}
                {author === "user" && (
                  <Text
                    mb={0}
                    alignSelf="flex-end"
                    textAlign="left"
                    fontWeight={500}
                    fontSize={isArtifactsPanelVisible ? "sm" : "md"}
                    whiteSpace="pre-wrap"
                  >
                    {renderUserMessageTags(content, {
                      scenarioJobs,
                      executionSteps,
                      artifacts,
                      chatMessages,
                      onArtifactClick,
                      lookupByName,
                    })}
                  </Text>
                )}
              </VStack>
            )}
          </Box>
        )}
        {author === "user" && formattedUserTime && (
          <Text fontSize="xs" color="gray.400" textAlign="right" mb={3} mt={0.5} ml={3} alignSelf="flex-end" w="100%">
            {formattedUserTime}
          </Text>
        )}
      </VStack>
    </HStack>
  );
};

export default React.memo(Message);
